import { Mail, Send } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ApiError } from "@/lib/api";
import {
  getPortalAccess,
  sendFillRequest,
  sendPortalInvite,
  type StaffPortalAccess,
  togglePortalAccess,
} from "@/features/portal/staff-api";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const PERMISSIONS = [
  {
    field: "documents" as const,
    title: "Can view documents",
    desc: "View and download published documents, and upload files.",
  },
  {
    field: "financials" as const,
    title: "Can enter financial info",
    desc: "Fill in the limited-field 433 financial worksheets.",
  },
];

/**
 * Staff-side "Client portal" card on the Client screen: toggles the taxpayer's
 * (party T) show_documents / show_financials access flags and lets a
 * practitioner email the client their onboarding link. Navy card per the
 * "Client profile" Calm Studio mockup.
 */
export function PortalAccessCard({ clientId }: { clientId: string }) {
  const [access, setAccess] = useState<StaffPortalAccess | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingField, setSavingField] = useState<"documents" | "financials" | null>(null);
  const [sending, setSending] = useState(false);
  const [inviting, setInviting] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    getPortalAccess(clientId)
      .then((rows) => {
        if (!active) return;
        setAccess(rows.find((r) => r.party === "T") ?? null);
      })
      .catch(() => active && setAccess(null))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [clientId]);

  // Absent taxpayer row → treat every flag as off until the firm turns it on.
  const checked = {
    documents: access?.showDocuments ?? false,
    financials: access?.showFinancials ?? false,
  };

  async function handleToggle(field: "documents" | "financials", next: boolean) {
    setSavingField(field);
    try {
      const updated = await togglePortalAccess(clientId, {
        party: "T",
        ...(field === "documents" ? { show_documents: next } : { show_financials: next }),
      });
      setAccess(updated);
      toast.success("Portal access updated.");
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't update portal access.";
      toast.error(message);
    } finally {
      setSavingField(null);
    }
  }

  async function handleSendFillRequest() {
    setSending(true);
    try {
      const { detail } = await sendFillRequest(clientId);
      toast.success(detail);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't send the fill request.";
      toast.error(message);
    } finally {
      setSending(false);
    }
  }

  async function handleSendPortalInvite() {
    setInviting(true);
    try {
      const { detail } = await sendPortalInvite(clientId);
      toast.success(detail);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : "Couldn't send the portal invite.";
      toast.error(message);
    } finally {
      setInviting(false);
    }
  }

  const isActive = access?.isActive ?? false;

  return (
    <Card className="bg-brand-panel relative gap-4 overflow-hidden border-0 p-6 text-white">
      <span
        aria-hidden
        className="bg-primary/30 pointer-events-none absolute -top-16 -right-16 size-56 rounded-full blur-3xl"
      />
      <div className="relative flex items-center justify-between gap-3">
        <h2 className="text-lg font-semibold">Client portal</h2>
        <span className="flex items-center gap-1.5 rounded-full bg-white/15 px-2.5 py-1 text-xs font-semibold">
          <span className={`size-[7px] rounded-full ${isActive ? "bg-success" : "bg-white/40"}`} />
          {isActive ? "Active" : "Not activated"}
        </span>
      </div>
      <p className="text-brand-panel-muted relative text-[13px]">
        Control what this client can see and do when they sign in.
      </p>

      <div className="relative flex flex-col gap-1.5">
        {PERMISSIONS.map((perm) => (
          <label
            key={perm.field}
            className="flex cursor-pointer items-start gap-3 rounded-sm bg-white/8 p-3"
          >
            <input
              type="checkbox"
              checked={checked[perm.field]}
              disabled={loading || savingField !== null}
              onChange={(e) => handleToggle(perm.field, e.target.checked)}
              className="mt-0.5 size-[18px] shrink-0 accent-[var(--primary)]"
            />
            <span className="flex flex-col gap-0.5 leading-snug">
              <span className="font-semibold">{perm.title}</span>
              <span className="text-brand-panel-muted text-xs">{perm.desc}</span>
            </span>
          </label>
        ))}
      </div>

      {access?.lastLoginAt && (
        <span className="text-sidebar-muted relative text-xs tabular-nums">
          Last login {formatWhen(access.lastLoginAt)}
        </span>
      )}

      <div className="relative grid grid-cols-2 gap-2">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              disabled={inviting}
              className="h-10 rounded-[11px] border border-white/20 bg-white/10 text-[13px] font-semibold text-white hover:bg-white/20"
            >
              <Mail /> Send invite
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send portal invite?</AlertDialogTitle>
              <AlertDialogDescription>
                This emails the client their portal link and how to sign in (they sign up with
                their email and the last 4 digits of their SSN or EIN, then set their own
                password). The client portal must be enabled in Settings › Portal.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSendPortalInvite}>Send</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              disabled={sending}
              className="text-brand-panel h-10 rounded-[11px] bg-white text-[13px] font-semibold hover:bg-white/90"
            >
              <Send /> Fill request
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Send fill request?</AlertDialogTitle>
              <AlertDialogDescription>
                This emails the client a secure onboarding link so they can complete their profile
                in the portal.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleSendFillRequest}>Send</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Card>
  );
}
